// src/components/Header/Header.jsx
import React from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import logoImage from "../../assets/images/IGNA Primary.png";
import {
  Logout,
  LogoutOutlined,
  Menu as MenuIcon,
  LightMode,
  DarkMode,
} from "@mui/icons-material";
import { NavLink, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import ConfirmDialog from "../Dialog/ConfirmDialog.jsx";
import { HEADER_NAVIGATION_ITEMS } from "./HeaderItems.js";
import { useThemeMode } from "../../context/ThemeContext";

const Header = () => {
  const theme = useTheme();
  const { mode, toggleTheme } = useThemeMode();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const { instance, accounts } = useMsal();
  const [logoutDialogOpen, setLogoutDialogOpen] = React.useState(false);
  const navigate = useNavigate();
  const openLogoutDialog = () => setLogoutDialogOpen(true);
  const closeLogoutDialog = () => setLogoutDialogOpen(false);

  const handleLogout = async () => {
    closeLogoutDialog();
    try {
      const logoutRequest = {
        account: accounts?.[0],
        // Always return user to login route after logout
        postLogoutRedirectUri: `${window.location.origin}/`,
      };

      // Use redirect logout to ensure we land on login page,
      // even if the user was on a "public" route.
      await instance.logoutRedirect(logoutRequest);
    } catch (error) {
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = `${window.location.origin}/`;
    }
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        color="inherit"
        sx={{
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Toolbar sx={{ minHeight: 67 }}>
          {isMobile && (
            <IconButton
              edge="start"
              onClick={() => setDrawerOpen(true)}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {!isMobile && (
            <Box sx={{ height: 32, display: "flex", alignItems: "center" }}>
              <img
                src={logoImage}
                alt="IGNA Scout"
                onClick={() => navigate("/dashboard")}
                style={{
                  cursor: "pointer",
                  display: "block",
                  height: "100%",
                  width: "auto",
                  objectFit: "contain",
                }}
              />
            </Box>
          )}

          {!isMobile && (
            <Box sx={{ display: "flex", gap: 1, ml: 3, py: 1.6 }}>
              {HEADER_NAVIGATION_ITEMS?.map((item) => (
                <Button
                  key={item.id}
                  size="large"
                  component={NavLink}
                  to={item.path}
                  startIcon={item.icon}
                  className={({ isActive, isPending }) =>
                    isPending ? "pending" : isActive ? "active" : ""
                  }
                  sx={{
                    fontWeight: 500,
                    borderRadius: 2,
                    px: 2,

                    color: theme.palette.text.primary,
                    textTransform: "none",
                    "&.active": {
                      color: theme.palette.primary.main,
                      backgroundColor: `${theme.palette.primary.main}16`,
                    },
                    "&.pending": {
                      opacity: 0.6,
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          <Box sx={{ flexGrow: 1 }} />

          {/* Theme Toggle Button */}
          <IconButton
            onClick={toggleTheme}
            sx={{
              mr: 2,
              color: theme.palette.text.primary,
            }}
            aria-label="toggle theme"
          >
            {mode === "dark" ? <LightMode /> : <DarkMode />}
          </IconButton>

          {!isMobile && (
            <Button
              startIcon={<LogoutOutlined />}
              onClick={openLogoutDialog}
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 500,
                textTransform: "none",
              }}
            >
              Logout
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.paper,
          },
        }}
      >
        <Box sx={{ width: 260 }}>
          <Typography variant="h6" sx={{ p: 2, fontWeight: 600 }}>
            IGNA Scout
          </Typography>

          <Divider />

          <List>
            {HEADER_NAVIGATION_ITEMS?.map((item) => (
              <ListItem key={item.id} disablePadding>
                <ListItemButton
                  component={NavLink}
                  to={item.path}
                  onClick={() => setDrawerOpen(false)}
                  className={({ isActive, isPending }) =>
                    isPending ? "pending" : isActive ? "active" : ""
                  }
                  sx={{
                    color: theme.palette.text.primary,
                    "&.active": {
                      backgroundColor: `${theme.palette.primary.main}16`,
                      color: theme.palette.primary.main,
                    },
                    "&.pending": {
                      opacity: 0.6,
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: "inherit" }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <Divider />

          <List>
            {/* Theme Toggle in Drawer */}
            <ListItem disablePadding>
              <ListItemButton onClick={toggleTheme}>
                <ListItemIcon sx={{ color: theme.palette.text.primary }}>
                  {mode === "dark" ? <LightMode /> : <DarkMode />}
                </ListItemIcon>
                <ListItemText
                  primary={mode === "dark" ? "Light Mode" : "Dark Mode"}
                  sx={{ color: theme.palette.text.primary }}
                />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  setDrawerOpen(false);
                  openLogoutDialog();
                }}
              >
                <ListItemIcon sx={{ color: theme.palette.text.primary }}>
                  <LogoutOutlined />
                </ListItemIcon>
                <ListItemText
                  primary="Logout"
                  sx={{ color: theme.palette.text.primary }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>

      <ConfirmDialog
        open={logoutDialogOpen}
        onClose={closeLogoutDialog}
        onConfirm={handleLogout}
        title="Confirm Logout"
        description="Are you sure you want to log out?"
        confirmText="Logout"
        cancelText="Cancel"
        confirmColor="error"
        confirmIcon={<Logout />}
      />
    </>
  );
};

export default Header;
